import { useMemo, useEffect, useCallback, useState } from "react"
import PropTypes from "prop-types"
import { isEmpty, chain, groupBy, compact, find, sortBy, findIndex, capitalize, min, isNil } from "lodash"
import { List, ListItem, Typography, IconButton } from "@mui/material"
import { Close, Lock } from "@mui/icons-material"
import { AvailableMetricsShape } from "./helpers/prop-types"
import "./styles.scss"

function recursiveFlattenAndFilter(items, selectedMetrics, options = {}) {
  return items.reduce((acc, item) => {
    const itemLabel = item.label || item.id
    if (!isEmpty(item.items)) {
      return [...acc, ...recursiveFlattenAndFilter(item.items, selectedMetrics, { isMetricItem: !isEmpty(item.metricTypeOptions), parentLabel: itemLabel })]
    }
    const selectedMetricIndex = findIndex(selectedMetrics, (metric) => metric === item.selector)
    let label = options.parentLabel
    if (options.isMetricItem && !item.isDefault) {
      label = `${options.parentLabel} - ${itemLabel}`
    }
    if (selectedMetricIndex !== -1) {
      return [...acc, {
        ...item,
        label,
        order: selectedMetricIndex,
        isMetricItem: options.isMetricItem,
        categoryPath: chain(item.selector)
          .split(".")
          .thru(paths => options.isMetricItem ? paths.slice(0, -2) : paths)
          .join(".")
          .value(),
      }]
    }
    return acc
  }, [])
}

function getCompactLabel(label, item, path) {
  if (!item) {
    return label
  }
  const itemLabel = item?.label || capitalize(path)
  return compact([label, itemLabel]).join(" - ")
}

function getCategoryLabel(categoryGroups, categoryPath) {
  let currentItem = categoryGroups
  const { label } = categoryPath.reduce(({ label, cumulativePath }, path) => {
    cumulativePath = compact([cumulativePath, path]).join(".")
    if (currentItem?.items) {
      // If the current item is the bottom level of the category, do not return the item,
      // unless it is a category without factors (e.g. exposure.market).
      currentItem = find(currentItem.items, ({ id, items, selector }) => {
        if (items && isEmpty(items)) {
          return false
        }
        if (id === path) {
          return true
        }
        // In the case of a category id that doesn't match the selector,
        // return the item if the selector matches the category path (e.g. risk.GlobalMarket).
        return selector === categoryPath.join(".")
      })
    } else if (currentItem && currentItem[cumulativePath]) {
      currentItem = currentItem[cumulativePath]
    }
    return { label: getCompactLabel(label, currentItem, path), cumulativePath }
  }, { label: "", cumulativePath: "" })
  return label
}

function getCategoryKey(categoryPath) {
  const keyBlocks = categoryPath.split(".")
  if (keyBlocks.length <= 2) {
    return categoryPath
  }
  return keyBlocks.slice(0, 2).join(".")
}

export default function MetricColumnOrdering({ availableMetrics, selectedMetrics, setSelectedMetrics }) {
  const [categories, setCategories] = useState({})

  const categoryGroups = useMemo(() => isEmpty(availableMetrics)
    ? []
    : Object.values(availableMetrics).reduce((allGroups, group) => {
      function recursiveReduceGroup(group) {
        if (!group.selector && group.items) {
          group.items.forEach(item => recursiveReduceGroup(item))
        } else {
          allGroups = { ...allGroups, [group.selector]: group }
        }
      }
      recursiveReduceGroup(group)
      return allGroups
    }, {}),
  [availableMetrics])

  const metrics = useMemo(() => recursiveFlattenAndFilter(Object.values(categoryGroups), selectedMetrics), [categoryGroups, selectedMetrics])
  const metricCategories = useMemo(() => groupBy(metrics, "categoryPath"), [metrics])

  const getCategories = useCallback(() => {
    return Object.entries(metricCategories).reduce((categoriesWithItems, [key, categoryItems]) => {
      const categoryPath = key.split(".")
      const categoryLabel = getCategoryLabel(categoryGroups, categoryPath)
      const categoryKey = getCategoryKey(key)
      const currentCategory = categoriesWithItems[categoryKey]
      const items = sortBy(categoryItems, "order")
      const order = currentCategory?.order
      const categoryWithItems = { items, label: categoryLabel, id: categoryKey, order }
      return { ...categoriesWithItems, [categoryKey]: categoryWithItems }
    }, {})
  }, [metricCategories, categoryGroups])

  const sortedCategories = useMemo(() => {
    return chain(categories)
      .map((category) => {
        if (!isNil(category.order)) {
          return category
        }
        return { ...category, order: min(category.items.map(item => item.order)) }
      })
      .sortBy("order")
      .value()
  }, [categories])

  useEffect(() => {
    setCategories(getCategories())
  }, [getCategories])

  return <div className="metrics-column-ordering">
    <div className="metrics-selection-header">
      <Typography color="inherit" noWrap>
        Selected Metrics
      </Typography>
    </div>
    <List>
      <ListItem className="locked-header">
        <div className="locked-header-label">
          Identifier
        </div>
        <div className="locked-header-icon">
          <Lock />
        </div>
      </ListItem>
      <div className="metrics-list">
        {sortedCategories.map(category =>
          <div key={category.id} className="metric-ordering-category">
            <ListItem className="category-item header" data-type="category" data-id={category.id}>
              {category.label}
            </ListItem>

            {category?.items?.map(item => <ListItem key={item.selector} className="metric-item">
              <div className="metric-item-label">
                <span className="metric-item-label-text">{item.label}</span>
              </div>
              <IconButton size="small" className="metric-item-action" onClick={() => setSelectedMetrics(metrics => metrics.filter(metric => metric !== item.selector))}>
                <Close fontSize="small" />
              </IconButton>
            </ListItem>,
            )}
          </div>,
        )}
      </div>
    </List>
  </div>
}

MetricColumnOrdering.propTypes = {
  availableMetrics: AvailableMetricsShape,
  selectedMetrics: PropTypes.object,
  setSelectedMetrics: PropTypes.func,
}
